import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRightLeft,
  BadgeCheck,
  BarChart3,
  FileSpreadsheet,
  Scale,
  Settings,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Download,
  Edit3,
  Eye,
  FileText,
  Hourglass,
  Inbox,
  Pencil,
  RotateCcw,
  Send,
  Users,
  X,
  XCircle
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SignaturePad from '../components/SignaturePad';
import Podpis from '../components/Podpis';
import SearchableUserSelect from '../components/SearchableUserSelect';
import PanelLimitowNadgodzin from '../components/PanelLimitowNadgodzin';
import nadgodzinyService from '../services/nadgodzinyService';
import { escapeHtml, podpisHtml } from '../utils/podpis';
import {
  biezacyMiesiac,
  czyPoczatekMinal,
  dataDoYmd,
  klasaPaska,
  formatData,
  formatDataGodzina,
  formatGodziny,
  obliczLiczbeGodzin,
  opisRozliczenia,
  sugerowanyDodatek,
  widziWszystkieWnioski
} from '../utils/nadgodziny';

const TABS = ['moje', 'do-decyzji', 'przeglad'];
const FILTRY = ['', 'oczekujacy', 'zatwierdzony', 'do-potwierdzenia', 'przekroczenie', 'do-rozliczenia', 'rozliczone', 'odrzucony', 'cofniete', 'przeterminowany'];

const STATUSY = {
  oczekujacy: { label: 'Oczekuje na decyzję', className: 'bg-yellow-100 text-yellow-800', Icon: Clock },
  zatwierdzony: { label: 'Zgoda', className: 'bg-green-100 text-green-800', Icon: CheckCircle },
  odrzucony: { label: 'Brak zgody', className: 'bg-red-100 text-red-800', Icon: XCircle },
  cofniete: { label: 'Cofnięty', className: 'bg-gray-100 text-gray-800', Icon: RotateCcw },
  przeterminowany: { label: 'Wygasł bez decyzji', className: 'bg-gray-200 text-gray-700', Icon: Hourglass }
};

const osoba = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || u.email : '');
const idOsoby = (u) => (u ? String(u._id || u) : '');

const Badge = ({ className, Icon, children }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
    {Icon && <Icon className="w-3 h-3 mr-1" />}
    {children}
  </span>
);

const StatusBadge = ({ w }) => {
  if (w.oczekujeNaPoprawke && w.status === 'oczekujacy') {
    return <Badge className="bg-orange-100 text-orange-800" Icon={AlertCircle}>Do poprawy</Badge>;
  }
  const { label, className, Icon } = STATUSY[w.status] || STATUSY.oczekujacy;
  return <Badge className={className} Icon={Icon}>{label}</Badge>;
};

const RealizacjaBadge = ({ w }) => {
  if (w.status !== 'zatwierdzony') return null;
  if (w.rozliczony) {
    return <Badge className="bg-emerald-600 text-white" Icon={BadgeCheck}>Rozliczono</Badge>;
  }
  if (w.przekroczenieDoAkceptacji) {
    return <Badge className="bg-amber-100 text-amber-800" Icon={AlertCircle}>Przekroczenie — do akceptacji przełożonego</Badge>;
  }
  if (w.realizacjaPotwierdzona) {
    const przekroczenie = w.faktycznaLiczbaGodzin > w.szacowanaLiczbaGodzin;
    return (
      <Badge className={przekroczenie ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'} Icon={ClipboardCheck}>
        Wykonano: {formatGodziny(w.faktycznaLiczbaGodzin)} — do rozliczenia
      </Badge>
    );
  }
  if (czyPoczatekMinal(w.planowanaData, w.godzinaOd)) {
    return <Badge className="bg-blue-50 text-blue-800" Icon={ClipboardCheck}>Czeka na potwierdzenie wykonania</Badge>;
  }
  return null;
};

const Modal = ({ title, onClose, children, maxWidth = 'max-w-2xl' }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
    <div className={`bg-white rounded-lg p-6 w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Pole = ({ label, children, className = '' }) => (
  <div className={className}>
    <p className="text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{children || '—'}</div>
  </div>
);

const opisFaktycznych = (w) =>
  (w.faktycznaLiczbaGodzin > 0
    ? `${w.faktycznaGodzinaOd} – ${w.faktycznaGodzinaDo} (${formatGodziny(w.faktycznaLiczbaGodzin)})`
    : 'Nadgodziny nie zostały wykonane');

const SzczegolyWniosku = ({ w }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <Pole label="Pracownik">{w.pracownikImie} {w.pracownikNazwisko}</Pole>
      <Pole label="Stanowisko / dział">{[w.stanowisko, w.dzial].filter(Boolean).join(' / ')}</Pole>
      <Pole label="Planowana data">{formatData(w.planowanaData)}</Pole>
      <Pole label="Godziny pracy">{w.godzinaOd} – {w.godzinaDo} ({formatGodziny(w.szacowanaLiczbaGodzin)})</Pole>
      <Pole label="Nazwa / nr projektu" className="col-span-2">{w.projekt}</Pole>
      <Pole label="Zakres prac" className="col-span-2">{w.zakresPrac}</Pole>
      <Pole label="Potrzeba biznesowa" className="col-span-2">{w.potrzebaBiznesowa}</Pole>
      <Pole label="Wniosek o czas wolny 1:1">{w.wnioskujeOCzasWolny ? 'Tak' : 'Nie'}</Pole>
      <Pole label="Złożono">{formatDataGodzina(w.createdAt)}</Pole>
      <Pole label="Bezpośredni przełożony">{osoba(w.przełożony)}</Pole>
      <Pole label="Status"><StatusBadge w={w} /></Pole>
    </div>

    {w.kontrolaLimitow?.limitRoczny > 0 && (
      <div className={`p-3 rounded-lg text-sm border ${w.przekroczenieLimituRocznego ? 'bg-red-50 border-red-200 text-red-900' : w.kontrolaLimitow.ostrzezenia?.length ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
        <p className="font-medium flex items-center gap-1.5">
          <Scale className="h-4 w-4" />
          Limity Kodeksu pracy (stan z {formatDataGodzina(w.kontrolaLimitow.data)})
        </p>
        <p className="mt-1">
          Rok {w.kontrolaLimitow.rok}: {formatGodziny(w.kontrolaLimitow.godzinyWRoku)} z {formatGodziny(w.kontrolaLimitow.limitRoczny)} ({w.kontrolaLimitow.procentLimitu}%)
          {w.kontrolaLimitow.tydzien && <> · tydzień {w.kontrolaLimitow.tydzien}: {formatGodziny(w.kontrolaLimitow.godzinyWTygodniu)} (limit {formatGodziny(w.kontrolaLimitow.limitTygodniowy)})</>}
        </p>
        {(w.kontrolaLimitow.ostrzezenia || []).map((o, i) => <p key={i} className="mt-1 text-xs flex items-start gap-1"><AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{o}</p>)}
        {w.przekroczenieLimituRocznego && (
          <p className="mt-1 text-xs font-medium">Zatwierdzono ponad roczny limit przez administratora. Uzasadnienie: {w.uzasadnieniePrzekroczeniaLimitu}</p>
        )}
      </div>
    )}

    {w.status === 'przeterminowany' && (
      <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700">
        Wniosek wygasł {formatDataGodzina(w.dataPrzeterminowania)} — planowany termin minął bez decyzji przełożonego.
      </div>
    )}

    {w.oczekujeNaPoprawke && w.aktywnyKomentarzZwrotu?.komentarz && (
      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-900">
        <p className="font-medium">Uwagi przełożonego ({w.aktywnyKomentarzZwrotu.zwrocilImieNazwisko})</p>
        <p className="mt-1 whitespace-pre-wrap">{w.aktywnyKomentarzZwrotu.komentarz}</p>
      </div>
    )}

    {w.historiaPrzekazan?.length > 0 && (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 space-y-1">
        <p className="font-medium">Przekazania wniosku</p>
        {w.historiaPrzekazan.map((p, i) => (
          <p key={i}>
            {formatDataGodzina(p.data)}: {p.zImieNazwisko || '—'} → <strong>{p.doImieNazwisko}</strong>
            {p.automatyczne ? ' (automatycznie — urlop przełożonego)' : ` (przekazanie: ${p.przekazalImieNazwisko})`}
            {p.powod ? ` — ${p.powod}` : ''}
          </p>
        ))}
      </div>
    )}

    {(w.status === 'zatwierdzony' || w.status === 'odrzucony') && (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
        <p className="text-sm font-semibold text-gray-900">Decyzja bezpośredniego przełożonego</p>
        <div className="grid grid-cols-2 gap-4">
          <Pole label="Decyzja">{w.status === 'zatwierdzony' ? 'Wyrażam zgodę (polecam wykonanie pracy)' : 'Nie wyrażam zgody'}</Pole>
          <Pole label="Data i godzina decyzji">{formatDataGodzina(w.dataZatwierdzenia)}</Pole>
          {w.status === 'zatwierdzony' && <Pole label="Sposób rozliczenia" className="col-span-2">{opisRozliczenia(w)}</Pole>}
          {w.status === 'odrzucony' && <Pole label="Powód braku zgody" className="col-span-2">{w.powodOdrzucenia}</Pole>}
          {w.uwagiLimity && <Pole label="Uwagi / limity" className="col-span-2">{w.uwagiLimity}</Pole>}
        </div>
        {w.decyzjaPoTerminie && (
          <div className="text-xs text-amber-800">
            <p className="flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />Decyzja podjęta po planowanym rozpoczęciu pracy.</p>
            {w.uzasadnieniePoTerminie && <p className="mt-1 whitespace-pre-wrap">Uzasadnienie: {w.uzasadnieniePoTerminie}</p>}
          </div>
        )}
        {w.podpisPrzełożonego && (
          <div>
            <Podpis value={w.podpisPrzełożonego} alt="Podpis przełożonego" className="max-w-[220px] max-h-16 object-contain" />
            <p className="mt-1 text-xs text-gray-700">{w.podpisPrzełożonegoImieNazwisko}</p>
          </div>
        )}
      </div>
    )}

    {w.realizacjaPotwierdzona && (
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg grid grid-cols-2 gap-4">
        <p className="col-span-2 text-sm font-semibold text-emerald-900">Potwierdzenie wykonania</p>
        <Pole label="Faktycznie przepracowano">{opisFaktycznych(w)}</Pole>
        <Pole label="Potwierdzono">{formatDataGodzina(w.dataPotwierdzeniaRealizacji)}</Pole>
        {w.realizacjaUwagi && <Pole label="Uwagi pracownika" className="col-span-2">{w.realizacjaUwagi}</Pole>}
        {w.przekroczenieDoAkceptacji && (
          <p className="col-span-2 text-xs text-amber-800 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />Czas przekracza zatwierdzony — czeka na akceptację przełożonego.
          </p>
        )}
        {w.przekroczenieAkceptacja?.data && (
          <Pole label="Przekroczenie zaakceptował" className="col-span-2">
            {w.przekroczenieAkceptacja.przezImieNazwisko}, {formatDataGodzina(w.przekroczenieAkceptacja.data)}
          </Pole>
        )}
        {w.historiaKorekt?.length > 0 && (
          <div className="col-span-2 text-xs text-emerald-900 space-y-1">
            <p className="font-medium">Korekty faktycznych godzin</p>
            {w.historiaKorekt.map((k, i) => (
              <p key={i}>
                {formatDataGodzina(k.data)}, {k.skorygowalImieNazwisko}: {k.poprzednieGodziny ?? 0} godz. → <strong>{k.noweGodziny} godz.</strong>
                {k.noweOd ? ` (${k.noweOd} – ${k.noweDo})` : ''} — {k.uzasadnienie}
              </p>
            ))}
          </div>
        )}
      </div>
    )}

    {w.rozliczony && (
      <div className="p-3 bg-emerald-600 text-white rounded-lg text-sm">
        <p className="font-medium flex items-center gap-1"><BadgeCheck className="h-4 w-4" />Rozliczono</p>
        <p className="mt-1">{w.rozliczenie?.przezImieNazwisko}, {formatDataGodzina(w.rozliczenie?.data)}{w.rozliczenie?.uwagi ? ` — ${w.rozliczenie.uwagi}` : ''}</p>
      </div>
    )}

    {w.podpisPracownika && (
      <div>
        <p className="text-xs text-gray-500 mb-1">Czytelny podpis pracownika</p>
        <Podpis value={w.podpisPracownika} alt="Podpis pracownika" className="max-w-[220px] max-h-16 object-contain" />
        <p className="mt-1 text-xs text-gray-700">{w.pracownikImie} {w.pracownikNazwisko}</p>
      </div>
    )}
  </div>
);

async function pobierzPdf(w) {
  const pole = (v) => escapeHtml(v || '') || '&nbsp;';
  const kratka = (zaznaczona) => `<span style="display:inline-block;width:18px;font-weight:700;">[${zaznaczona ? 'X' : '&nbsp;&nbsp;'}]</span>`;
  const naglowek = (tekst) => `<tr><td colspan="2" style="background:#f0f0f0;border:1px solid #bbb;padding:10px;font-weight:700;">${tekst}</td></tr>`;
  const wiersz = (label, value) => `<tr><td style="border:1px solid #bbb;padding:9px;width:34%;font-weight:700;vertical-align:top;">${label}</td><td style="border:1px solid #bbb;padding:9px;white-space:pre-wrap;">${value}</td></tr>`;
  const podpis = (wartosc, nazwa, opis) => `
    <div style="text-align:center;min-width:220px;">
      <div style="height:60px;display:flex;align-items:flex-end;justify-content:center;">${podpisHtml(wartosc, { maxWidth: 180, maxHeight: 60, fontSize: 28 })}</div>
      <div style="border-top:1px dotted #555;margin-top:4px;padding-top:2px;font-size:11px;">${escapeHtml(nazwa || '')}</div>
      <div style="font-size:11px;color:#333;">(${opis})</div>
    </div>`;
  const zgoda = w.status === 'zatwierdzony';
  const odmowa = w.status === 'odrzucony';
  const dodatekTekst = w.sposobRozliczenia === 'wyplata' ? ` — <strong>${w.dodatekProcent}%</strong>` : '';
  const czasWolnyTekst = w.sposobRozliczenia === 'czas_wolny' ? ` — <strong>${w.wnioskujeOCzasWolny ? '1:1' : '1:1,5'}</strong>` : '';
  const korekty = (w.historiaKorekt || []).map((k) =>
    `${formatDataGodzina(k.data)}, ${escapeHtml(k.skorygowalImieNazwisko)}: ${k.poprzednieGodziny ?? 0} → ${k.noweGodziny} godz. (${escapeHtml(k.uzasadnienie)})`
  ).join('<br/>');

  const html = `
    <div id="pdf-nadgodziny" style="font-family:Arial,sans-serif;width:794px;padding:44px 56px;box-sizing:border-box;background:#fff;color:#000;font-size:12.5px;line-height:1.45;">
      <div style="text-align:right;">${escapeHtml(w.miejscowosc || '....................')}, dnia ${formatData(w.dataZlozenia || w.createdAt)} r.</div>
      <div style="text-align:right;font-size:11px;color:#333;margin-bottom:22px;">(Miejscowość i data złożenia wniosku)</div>
      <div style="text-align:center;font-weight:700;font-size:14px;">PRZEDWSTĘPNY WNIOSEK O ZGODĘ NA PRACĘ W GODZINACH NADLICZBOWYCH</div>
      <div style="text-align:center;font-style:italic;font-size:11.5px;margin-bottom:18px;">(Wymagana akceptacja PRZEŁOŻONEGO przed rozpoczęciem prac)</div>
      <table style="border-collapse:collapse;width:100%;margin-bottom:14px;">
        ${naglowek('1. DANE PRACOWNIKA')}
        ${wiersz('Imię i nazwisko:', pole(`${w.pracownikImie} ${w.pracownikNazwisko}`))}
        ${wiersz('Stanowisko i Dział:', pole([w.stanowisko, w.dzial].filter(Boolean).join(' / ')))}
        ${naglowek('2. SZCZEGÓŁY DOTYCZĄCE NADGODZIN')}
        ${wiersz('Planowana data:', formatData(w.planowanaData))}
        ${wiersz('Godziny pracy:', `od godz. ${escapeHtml(w.godzinaOd)} do godz. ${escapeHtml(w.godzinaDo)}`)}
        ${wiersz('Szacowana liczba godzin:', formatGodziny(w.szacowanaLiczbaGodzin))}
        ${wiersz('Nazwa / Nr projektu:', pole(w.projekt))}
        ${naglowek('3. OPIS ZADANIA I UZASADNIENIE (Wypełnia pracownik przed rozpoczęciem prac)')}
        ${wiersz('Zakres prac:<br/><span style="font-weight:400">(Co dokładnie zostanie zrobione?)</span>', pole(w.zakresPrac))}
        ${wiersz('Potrzeba biznesowa:<br/><span style="font-weight:400">(Dlaczego poza normalnymi godzinami?)</span>', pole(w.potrzebaBiznesowa))}
        ${wiersz('Wniosek o czas wolny:', w.wnioskujeOCzasWolny ? 'Wnioskuję o udzielenie czasu wolnego w wymiarze 1:1' : 'Nie')}
      </table>
      <div style="display:flex;justify-content:flex-end;margin-bottom:20px;">
        ${podpis(w.podpisPracownika, `${w.pracownikImie} ${w.pracownikNazwisko}`, 'Czytelny podpis Pracownika')}
      </div>
      <table style="border-collapse:collapse;width:100%;">
        ${naglowek('4. DECYZJA BEZPOŚREDNIEGO PRZEŁOŻONEGO (Kierownika / Dyrektora / Członka Zarządu)')}
        ${wiersz('Decyzja:', `${kratka(zgoda)} WYRAŻAM ZGODĘ (polecam wykonanie pracy)<br/>${kratka(odmowa)} NIE WYRAŻAM ZGODY${odmowa && w.powodOdrzucenia ? `<br/><em>Powód: ${escapeHtml(w.powodOdrzucenia)}</em>` : ''}`)}
        ${wiersz('Sposób rozliczenia:', `${kratka(w.sposobRozliczenia === 'wyplata')} Wypłata wynagrodzenia wraz z dodatkiem (50% / 100%)${dodatekTekst}<br/>${kratka(w.sposobRozliczenia === 'czas_wolny')} Udzielenie czasu wolnego (1:1 na pisemny wniosek pracownika)${czasWolnyTekst}`)}
        ${wiersz('Uwagi / Limity:', pole(w.uwagiLimity))}
      </table>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:22px;">
        <div style="text-align:center;min-width:220px;">
          <div style="height:60px;display:flex;align-items:flex-end;justify-content:center;">${w.dataZatwierdzenia ? formatDataGodzina(w.dataZatwierdzenia) : ''}</div>
          <div style="border-top:1px dotted #555;margin-top:4px;font-size:11px;">(Data i godzina zatwierdzenia)</div>
        </div>
        ${podpis(w.podpisPrzełożonego, w.podpisPrzełożonegoImieNazwisko, 'Podpis Przełożonego')}
      </div>
      ${w.decyzjaPoTerminie ? `<p style="margin-top:12px;font-size:11px;color:#9a3412;">Uwaga: decyzja podjęta po planowanym rozpoczęciu pracy.${w.uzasadnieniePoTerminie ? ` Uzasadnienie: ${escapeHtml(w.uzasadnieniePoTerminie)}` : ''}</p>` : ''}
      ${w.realizacjaPotwierdzona ? `
        <table style="border-collapse:collapse;width:100%;margin-top:18px;">
          ${naglowek('5. POTWIERDZENIE WYKONANIA (Wypełnia pracownik po wykonaniu prac)')}
          ${wiersz('Faktycznie przepracowano:', w.faktycznaLiczbaGodzin > 0 ? `od godz. ${escapeHtml(w.faktycznaGodzinaOd)} do godz. ${escapeHtml(w.faktycznaGodzinaDo)} — ${formatGodziny(w.faktycznaLiczbaGodzin)}` : 'Nadgodziny nie zostały wykonane')}
          ${wiersz('Data potwierdzenia:', formatDataGodzina(w.dataPotwierdzeniaRealizacji))}
          ${w.realizacjaUwagi ? wiersz('Uwagi:', pole(w.realizacjaUwagi)) : ''}
          ${w.przekroczenieAkceptacja?.data ? wiersz('Przekroczenie zaakceptował:', `${escapeHtml(w.przekroczenieAkceptacja.przezImieNazwisko)}, ${formatDataGodzina(w.przekroczenieAkceptacja.data)}`) : ''}
          ${korekty ? wiersz('Korekty:', korekty) : ''}
        </table>` : ''}
      ${w.rozliczony ? `
        <table style="border-collapse:collapse;width:100%;margin-top:18px;">
          ${naglowek('6. ROZLICZENIE (Wypełnia dział kadr)')}
          ${wiersz('Rozliczono:', `${escapeHtml(w.rozliczenie?.przezImieNazwisko)}, ${formatDataGodzina(w.rozliczenie?.data)}`)}
          ${w.rozliczenie?.uwagi ? wiersz('Uwagi:', pole(w.rozliczenie.uwagi)) : ''}
        </table>` : ''}
    </div>`;

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1;';
  container.innerHTML = html;
  document.body.appendChild(container);
  try {
    if (document.fonts?.load) await document.fonts.load("28px 'Caveat'").catch(() => {});
    await new Promise((resolve) => setTimeout(resolve, 100));
    const canvas = await html2canvas(container.querySelector('#pdf-nadgodziny'), {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');
    let offset = 0;
    while (offset < imgHeight) {
      if (offset > 0) doc.addPage();
      doc.addImage(imgData, 'PNG', 0, -offset, pageWidth, imgHeight);
      offset += pageHeight;
    }
    doc.save(`wniosek-nadgodziny-${w.pracownikNazwisko || 'pracownik'}-${dataDoYmd(w.planowanaData)}.pdf`.replace(/\s/g, '-'));
  } finally {
    document.body.removeChild(container);
  }
}

const Nadgodziny = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabZUrl = searchParams.get('tab');
  const mainTab = TABS.includes(tabZUrl) ? tabZUrl : 'moje';
  const setTab = (tab) => setSearchParams({ tab });

  const widziWszystkie = widziWszystkieWnioski(user);
  const jestSuperAdmin = user?.role === 'super admin';
  const jestHR = user?.department === 'HR' || jestSuperAdmin;
  const mozeDecydowac = (w) => jestSuperAdmin || idOsoby(w.przełożony) === String(user?._id);

  const [szczegolyId, setSzczegolyId] = useState(null);
  const [decyzjaDla, setDecyzjaDla] = useState(null);
  const [zwrotDla, setZwrotDla] = useState(null);
  const [przekazDla, setPrzekazDla] = useState(null);
  const [realizacjaDla, setRealizacjaDla] = useState(null);
  const [korektaDla, setKorektaDla] = useState(null);
  const [rozliczDla, setRozliczDla] = useState(null);
  const [konfiguracjaOtwarta, setKonfiguracjaOtwarta] = useState(false);
  const [pokazPodsumowanie, setPokazPodsumowanie] = useState(false);
  const [eksportWToku, setEksportWToku] = useState(null);
  const filtrZUrl = searchParams.get('filtr');
  const [filtrStatus, setFiltrStatus] = useState(FILTRY.includes(filtrZUrl) ? filtrZUrl : '');
  const [filtrMiesiac, setFiltrMiesiac] = useState('');
  const [filtrSzukaj, setFiltrSzukaj] = useState('');
  const [pdfWToku, setPdfWToku] = useState(null);

  const { data: mojeData, isLoading: mojeLoading } = useQuery('nadgodziny-moje', nadgodzinyService.getMoje, {
    refetchInterval: 30000,
    enabled: !!user
  });
  const { data: doDecyzjiData, isLoading: doDecyzjiLoading } = useQuery('nadgodziny-do-decyzji', nadgodzinyService.getDoDecyzji, {
    refetchInterval: 30000,
    enabled: !!user
  });
  const pokazPrzeglad = widziWszystkie || !!user?.isPrzelozony;
  const { data: przegladData, isLoading: przegladLoading } = useQuery('nadgodziny-przeglad', nadgodzinyService.getPrzeglad, {
    refetchInterval: 60000,
    enabled: !!user && pokazPrzeglad
  });
  const { data: szczegolyData, isFetching: szczegolyLoading } = useQuery(
    ['wniosek-nadgodziny', szczegolyId],
    () => nadgodzinyService.getWniosek(szczegolyId),
    { enabled: !!szczegolyId }
  );

  const moje = mojeData?.wnioski || [];
  const doDecyzji = doDecyzjiData?.wnioski || [];
  const przeglad = useMemo(() => przegladData?.wnioski || [], [przegladData]);
  const mozeRozliczac = !!przegladData?.mozeRozliczac;
  const pokazDoDecyzji = !!user?.isPrzelozony || jestSuperAdmin || doDecyzji.length > 0;

  useEffect(() => {
    if (mainTab === 'przeglad' && user && !pokazPrzeglad) setSearchParams({ tab: 'moje' });
  }, [mainTab, user, pokazPrzeglad, setSearchParams]);

  const odswiez = () => {
    queryClient.invalidateQueries('nadgodziny-moje');
    queryClient.invalidateQueries('nadgodziny-do-decyzji');
    queryClient.invalidateQueries('nadgodziny-przeglad');
    queryClient.invalidateQueries('nadgodziny-licznik');
    queryClient.invalidateQueries('wniosek-nadgodziny');
  };
  const bladMutacji = (error) => toast.error(error.response?.data?.message || 'Wystąpił błąd');

  const cofnijMutation = useMutation((id) => nadgodzinyService.cofnij(id), {
    onSuccess: () => { odswiez(); toast.success('Wniosek został cofnięty'); },
    onError: bladMutacji
  });
  const akceptujMutation = useMutation((id) => nadgodzinyService.akceptujPrzekroczenie(id), {
    onSuccess: () => { odswiez(); toast.success('Przekroczony czas zaakceptowany — HR może rozliczyć nadgodziny'); },
    onError: bladMutacji
  });

  const przegladFiltrowany = useMemo(() => {
    const szukaj = filtrSzukaj.trim().toLowerCase();
    return przeglad.filter((w) => {
      switch (filtrStatus) {
        case 'do-potwierdzenia':
          if (w.status !== 'zatwierdzony' || w.realizacjaPotwierdzona) return false;
          break;
        case 'przekroczenie':
          if (!w.przekroczenieDoAkceptacji) return false;
          break;
        case 'do-rozliczenia':
          if (w.status !== 'zatwierdzony' || !w.realizacjaPotwierdzona || w.rozliczony || w.przekroczenieDoAkceptacji) return false;
          break;
        case 'rozliczone':
          if (!w.rozliczony) return false;
          break;
        case '':
          break;
        default:
          if (w.status !== filtrStatus) return false;
      }
      if (filtrMiesiac && !dataDoYmd(w.planowanaData).startsWith(filtrMiesiac)) return false;
      if (szukaj && !`${w.pracownikImie} ${w.pracownikNazwisko} ${w.projekt || ''}`.toLowerCase().includes(szukaj)) return false;
      return true;
    });
  }, [przeglad, filtrStatus, filtrMiesiac, filtrSzukaj]);

  const podsumowanie = useMemo(() => {
    const zatwierdzone = przegladFiltrowany.filter((w) => w.status === 'zatwierdzony');
    return {
      zatwierdzoneGodziny: zatwierdzone.reduce((s, w) => s + (w.szacowanaLiczbaGodzin || 0), 0),
      wykonaneGodziny: zatwierdzone.reduce((s, w) => s + (w.realizacjaPotwierdzona ? w.faktycznaLiczbaGodzin || 0 : 0), 0),
      oczekujace: przegladFiltrowany.filter((w) => w.status === 'oczekujacy').length,
      doPotwierdzenia: zatwierdzone.filter((w) => !w.realizacjaPotwierdzona).length,
      doRozliczenia: zatwierdzone.filter((w) => w.realizacjaPotwierdzona && !w.rozliczony && !w.przekroczenieDoAkceptacji).length,
      doAkceptacji: zatwierdzone.filter((w) => w.przekroczenieDoAkceptacji).length
    };
  }, [przegladFiltrowany]);

  const handleEksport = async (format) => {
    const miesiac = filtrMiesiac || biezacyMiesiac();
    setEksportWToku(format);
    try {
      await nadgodzinyService.pobierzEksport(miesiac, format);
      toast.success(`Eksport ${format.toUpperCase()} za ${miesiac} został pobrany`);
    } catch (err) {
      console.error('Błąd eksportu nadgodzin:', err);
      toast.error('Nie udało się pobrać eksportu');
    } finally {
      setEksportWToku(null);
    }
  };

  const handlePdf = async (w) => {
    setPdfWToku(w._id);
    try {
      // Listy nie zawierają podpisów — pobierz pełny wniosek
      const pelny = szczegolyData?.wniosek?._id === w._id ? szczegolyData.wniosek : (await nadgodzinyService.getWniosek(w._id)).wniosek;
      await pobierzPdf(pelny);
      toast.success('PDF został pobrany');
    } catch (err) {
      console.error('Błąd generowania PDF:', err);
      toast.error(err.response?.data?.message || 'Nie udało się wygenerować PDF');
    } finally {
      setPdfWToku(null);
    }
  };

  const akcja = (onClick, Icon, label, klasa = 'border border-gray-300 hover:bg-gray-50', disabled = false) => (
    <button key={label} onClick={onClick} disabled={disabled} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50 ${klasa}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  const renderKarta = (w, tryb) => {
    const mojaAkcja = tryb === 'moje';
    const jestemAutorem = idOsoby(w.createdBy) === String(user?._id);
    const mozePotwierdzic = mojaAkcja && w.status === 'zatwierdzony' && !w.realizacjaPotwierdzona && czyPoczatekMinal(w.planowanaData, w.godzinaOd);
    const mozePrzekazac = !mojaAkcja && widziWszystkie && w.status === 'oczekujacy';
    const mozeAkceptowacPrzekroczenie = !mojaAkcja && w.przekroczenieDoAkceptacji && mozeDecydowac(w);
    const mozeKorygowac = !mojaAkcja && w.status === 'zatwierdzony' && w.realizacjaPotwierdzona && !w.rozliczony && (mozeDecydowac(w) || jestHR) && !jestemAutorem;
    const mozeRozliczyc = !mojaAkcja && mozeRozliczac && w.status === 'zatwierdzony' && w.realizacjaPotwierdzona && !w.rozliczony && !w.przekroczenieDoAkceptacji;
    const maPdf = !['oczekujacy', 'cofniete', 'przeterminowany'].includes(w.status);

    return (
      <div key={w._id} className="bg-white shadow rounded-lg p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h3 className="text-base font-semibold text-gray-900 mr-2">{w.pracownikImie} {w.pracownikNazwisko}</h3>
              <StatusBadge w={w} />
              <RealizacjaBadge w={w} />
              {w.decyzjaPoTerminie && <Badge className="bg-amber-100 text-amber-800">Decyzja po terminie</Badge>}
              {w.przekroczenieLimituRocznego && <Badge className="bg-red-100 text-red-800" Icon={Scale}>Zgoda ponad limit roczny</Badge>}
              {!w.przekroczenieLimituRocznego && w.kontrolaLimitow?.ostrzezenia?.length > 0 && ['oczekujacy', 'zatwierdzony'].includes(w.status) && (
                <Badge className="bg-amber-50 text-amber-800" Icon={Scale}>Limit: {w.kontrolaLimitow.procentLimitu}% roku{w.kontrolaLimitow.godzinyWTygodniu > w.kontrolaLimitow.limitTygodniowy ? `, ${formatGodziny(w.kontrolaLimitow.godzinyWTygodniu)} w tyg.` : ''}</Badge>
              )}
              {w.historiaPrzekazan?.length > 0 && <Badge className="bg-blue-50 text-blue-800" Icon={ArrowRightLeft}>Przekazany</Badge>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <Pole label="Data">{formatData(w.planowanaData)}</Pole>
              <Pole label="Godziny">{w.godzinaOd} – {w.godzinaDo}</Pole>
              <Pole label="Liczba godzin">{formatGodziny(w.szacowanaLiczbaGodzin)}</Pole>
              <Pole label="Projekt">{w.projekt}</Pole>
            </div>
            <p className="text-sm text-gray-700 line-clamp-2"><span className="text-gray-500">Zakres prac: </span>{w.zakresPrac}</p>
            {mojaAkcja && w.oczekujeNaPoprawke && w.status === 'oczekujacy' && w.aktywnyKomentarzZwrotu?.komentarz && (
              <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-900">
                <p className="font-medium">Uwagi przełożonego:</p>
                <p className="whitespace-pre-wrap">{w.aktywnyKomentarzZwrotu.komentarz}</p>
              </div>
            )}
            {w.przekroczenieDoAkceptacji && !mojaAkcja && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <p className="font-medium">Faktyczny czas {formatGodziny(w.faktycznaLiczbaGodzin)} przekracza zatwierdzone {formatGodziny(w.szacowanaLiczbaGodzin)}</p>
                {w.realizacjaUwagi && <p className="whitespace-pre-wrap">Uzasadnienie pracownika: {w.realizacjaUwagi}</p>}
              </div>
            )}
            <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
              {[w.stanowisko, w.dzial].filter(Boolean).length > 0 && <span>{[w.stanowisko, w.dzial].filter(Boolean).join(' / ')}</span>}
              <span>Złożono: {formatDataGodzina(w.createdAt)}</span>
              {tryb !== 'do-decyzji' && w.przełożony && <span>Przełożony: {osoba(w.przełożony)}</span>}
              {w.status === 'zatwierdzony' && <span>Rozliczenie: {opisRozliczenia(w)}</span>}
              {w.wnioskujeOCzasWolny && w.status === 'oczekujacy' && <span>Pracownik wnioskuje o czas wolny 1:1</span>}
              {w.rozliczony && <span>Rozliczono: {formatDataGodzina(w.rozliczenie?.data)} ({w.rozliczenie?.przezImieNazwisko})</span>}
            </div>
          </div>
          <div className="flex flex-row lg:flex-col flex-wrap gap-2 lg:w-56 lg:flex-shrink-0">
            {akcja(() => setSzczegolyId(w._id), Eye, 'Szczegóły')}
            {tryb === 'do-decyzji' && (
              <>
                {akcja(() => setDecyzjaDla(w), CheckCircle, 'Podejmij decyzję', 'bg-blue-600 text-white hover:bg-blue-700')}
                {akcja(() => setZwrotDla(w), RotateCcw, 'Zwróć do poprawy', 'bg-orange-600 text-white hover:bg-orange-700')}
              </>
            )}
            {mozePrzekazac && akcja(() => setPrzekazDla(w), ArrowRightLeft, 'Przekaż innej osobie')}
            {mozeAkceptowacPrzekroczenie && akcja(
              () => { if (window.confirm(`Zaakceptować ${formatGodziny(w.faktycznaLiczbaGodzin)} zamiast zatwierdzonych ${formatGodziny(w.szacowanaLiczbaGodzin)}?`)) akceptujMutation.mutate(w._id); },
              BadgeCheck, 'Akceptuj przekroczenie', 'bg-amber-600 text-white hover:bg-amber-700', akceptujMutation.isLoading
            )}
            {mozeKorygowac && akcja(() => setKorektaDla(w), Pencil, w.przekroczenieDoAkceptacji ? 'Skoryguj godziny' : 'Koryguj godziny')}
            {mozeRozliczyc && akcja(() => setRozliczDla(w), BadgeCheck, 'Rozlicz', 'bg-emerald-600 text-white hover:bg-emerald-700')}
            {mojaAkcja && w.oczekujeNaPoprawke && w.status === 'oczekujacy' && (
              <Link to={`/nadgodziny/wniosek/${w._id}`} className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm">
                <Edit3 className="h-4 w-4" />Popraw wniosek
              </Link>
            )}
            {mozePotwierdzic && akcja(() => setRealizacjaDla(w), ClipboardCheck, 'Potwierdź wykonanie', 'bg-emerald-600 text-white hover:bg-emerald-700')}
            {mojaAkcja && w.status === 'oczekujacy' && akcja(
              () => { if (window.confirm('Czy na pewno chcesz wycofać ten wniosek?')) cofnijMutation.mutate(w._id); },
              RotateCcw, 'Cofnij', undefined, cofnijMutation.isLoading
            )}
            {maPdf && akcja(() => handlePdf(w), Download, pdfWToku === w._id ? 'Generowanie…' : 'Pobierz PDF', undefined, pdfWToku === w._id)}
          </div>
        </div>
      </div>
    );
  };

  const lista = mainTab === 'moje' ? moje : mainTab === 'do-decyzji' ? doDecyzji : przegladFiltrowany;
  const ladowanie = mainTab === 'moje' ? mojeLoading : mainTab === 'do-decyzji' ? doDecyzjiLoading : przegladLoading;
  const pustyKomunikat = {
    moje: 'Nie masz jeszcze wniosków o nadgodziny.',
    'do-decyzji': 'Brak wniosków oczekujących na Twoją decyzję.',
    przeglad: 'Brak wniosków spełniających kryteria.'
  }[mainTab];

  const zakladka = (tab, Icon, label, count) => (
    <button
      onClick={() => setTab(tab)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${mainTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}{count != null ? ` (${count})` : ''}</span>
    </button>
  );

  const kafelek = (label, wartosc, kolor, filtr) => (
    <button
      type="button"
      onClick={() => filtr != null && setFiltrStatus(filtrStatus === filtr ? '' : filtr)}
      className={`rounded-lg p-3 text-left ${kolor} ${filtr != null ? 'hover:ring-2 hover:ring-offset-1 hover:ring-blue-300' : 'cursor-default'} ${filtrStatus === filtr && filtr ? 'ring-2 ring-blue-500' : ''}`}
    >
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold">{wartosc}</p>
    </button>
  );

  const szczegoly = szczegolyData?.wniosek?._id === szczegolyId
    ? szczegolyData.wniosek
    : [...moje, ...doDecyzji, ...przeglad].find((w) => w._id === szczegolyId);

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      <div className="bg-white shadow rounded-lg p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Wnioski o nadgodziny</h2>
          <p className="text-sm text-gray-500">Zgoda przełożonego przed rozpoczęciem pracy w godzinach nadliczbowych</p>
        </div>
        <Link to="/nadgodziny/wniosek" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm self-start">
          <FileText className="h-4 w-4" />Nowy wniosek
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-2 sm:p-4">
        <div className="flex flex-wrap gap-2">
          {zakladka('moje', Inbox, 'Moje wnioski', moje.length)}
          {pokazDoDecyzji && zakladka('do-decyzji', Send, jestSuperAdmin ? 'Do decyzji (wszystkie)' : 'Do decyzji', doDecyzji.length)}
          {pokazPrzeglad && zakladka('przeglad', Users, widziWszystkie ? 'Wszystkie wnioski' : 'Rozpatrzone przeze mnie')}
        </div>

        {mainTab === 'przeglad' && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
            <div className="flex flex-wrap gap-3">
              <select value={filtrStatus} onChange={(e) => setFiltrStatus(e.target.value)} className="input max-w-[260px]">
                <option value="">Wszystkie statusy</option>
                <option value="oczekujacy">Oczekujące na decyzję</option>
                <option value="zatwierdzony">Zatwierdzone</option>
                <option value="do-potwierdzenia">Zatwierdzone — bez potwierdzenia wykonania</option>
                <option value="przekroczenie">Przekroczenie — do akceptacji przełożonego</option>
                <option value="do-rozliczenia">Do rozliczenia przez HR</option>
                <option value="rozliczone">Rozliczone</option>
                <option value="odrzucony">Bez zgody</option>
                <option value="cofniete">Cofnięte</option>
                <option value="przeterminowany">Wygasłe bez decyzji</option>
              </select>
              <input type="month" value={filtrMiesiac} onChange={(e) => setFiltrMiesiac(e.target.value)} className="input max-w-[180px]" title="Miesiąc planowanej pracy" />
              <input type="search" value={filtrSzukaj} onChange={(e) => setFiltrSzukaj(e.target.value)} placeholder="Szukaj pracownika lub projektu" className="input max-w-[260px]" />
              <div className="flex flex-wrap gap-2 sm:ml-auto">
                <button
                  type="button"
                  onClick={() => setPokazPodsumowanie((v) => !v)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm border ${pokazPodsumowanie ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <BarChart3 className="h-4 w-4" />Godziny w roku vs limit
                </button>
                {jestHR && (
                  <>
                    <button type="button" onClick={() => handleEksport('xlsx')} disabled={!!eksportWToku} title={`Eksport dla płac za ${filtrMiesiac || biezacyMiesiac()}`} className="px-3 py-2 rounded-lg flex items-center gap-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                      <FileSpreadsheet className="h-4 w-4" />{eksportWToku === 'xlsx' ? 'Generowanie…' : `XLSX ${filtrMiesiac || biezacyMiesiac()}`}
                    </button>
                    <button type="button" onClick={() => handleEksport('csv')} disabled={!!eksportWToku} title={`Eksport dla płac za ${filtrMiesiac || biezacyMiesiac()}`} className="px-3 py-2 rounded-lg flex items-center gap-2 text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                      <Download className="h-4 w-4" />{eksportWToku === 'csv' ? 'Generowanie…' : 'CSV'}
                    </button>
                    <button type="button" onClick={() => setKonfiguracjaOtwarta(true)} className="px-3 py-2 rounded-lg flex items-center gap-2 text-sm border border-gray-300 hover:bg-gray-50" title="Limity nadgodzin (Kodeks pracy)">
                      <Settings className="h-4 w-4" />Limity
                    </button>
                  </>
                )}
              </div>
            </div>
            {pokazPodsumowanie && (
              <PodsumowanieRoczne
                domyslnyRok={Number((filtrMiesiac || biezacyMiesiac()).slice(0, 4))}
                onWybierzPracownika={(p) => { setFiltrSzukaj(`${p.imie} ${p.nazwisko}`); setFiltrMiesiac(''); }}
              />
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {kafelek('Oczekujące', podsumowanie.oczekujace, 'bg-yellow-50 text-yellow-700', 'oczekujacy')}
              {kafelek('Zatwierdzone godziny', formatGodziny(podsumowanie.zatwierdzoneGodziny), 'bg-green-50 text-green-700', 'zatwierdzony')}
              {kafelek('Potwierdzone wykonane', formatGodziny(podsumowanie.wykonaneGodziny), 'bg-emerald-50 text-emerald-700', null)}
              {kafelek('Bez potwierdzenia', podsumowanie.doPotwierdzenia, 'bg-blue-50 text-blue-700', 'do-potwierdzenia')}
              {kafelek('Przekroczenia do akceptacji', podsumowanie.doAkceptacji, 'bg-amber-50 text-amber-700', 'przekroczenie')}
              {kafelek('Do rozliczenia', podsumowanie.doRozliczenia, 'bg-emerald-100 text-emerald-800', 'do-rozliczenia')}
            </div>
          </div>
        )}
      </div>

      {ladowanie ? (
        <div className="bg-white shadow rounded-lg p-12"><LoadingSpinner /></div>
      ) : lista.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Clock className="mx-auto h-14 w-14 text-gray-400 mb-4" />
          <p className="text-gray-500 mb-6">{pustyKomunikat}</p>
          {mainTab === 'moje' && (
            <Link to="/nadgodziny/wniosek" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <FileText className="h-4 w-4 mr-2" />Złóż wniosek o nadgodziny
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">{lista.map((w) => renderKarta(w, mainTab))}</div>
      )}

      {szczegolyId && szczegoly && (
        <Modal title="Wniosek o nadgodziny" onClose={() => setSzczegolyId(null)}>
          {szczegolyLoading && <p className="mb-3 text-xs text-gray-500">Wczytywanie pełnych danych…</p>}
          <SzczegolyWniosku w={szczegoly} />
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <button onClick={() => setSzczegolyId(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Zamknij</button>
            {!['oczekujacy', 'cofniete', 'przeterminowany'].includes(szczegoly.status) && (
              <button onClick={() => handlePdf(szczegoly)} disabled={!!pdfWToku} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                <Download className="h-4 w-4" />Pobierz PDF
              </button>
            )}
          </div>
        </Modal>
      )}

      {decyzjaDla && (
        <DecyzjaModal w={decyzjaDla} user={user} onClose={() => setDecyzjaDla(null)} onDone={() => { setDecyzjaDla(null); odswiez(); }} />
      )}
      {zwrotDla && (
        <ZwrotModal w={zwrotDla} onClose={() => setZwrotDla(null)} onDone={() => { setZwrotDla(null); odswiez(); }} />
      )}
      {przekazDla && (
        <PrzekazModal w={przekazDla} onClose={() => setPrzekazDla(null)} onDone={() => { setPrzekazDla(null); odswiez(); }} />
      )}
      {realizacjaDla && (
        <RealizacjaModal w={realizacjaDla} onClose={() => setRealizacjaDla(null)} onDone={() => { setRealizacjaDla(null); odswiez(); }} />
      )}
      {korektaDla && (
        <KorektaModal w={korektaDla} onClose={() => setKorektaDla(null)} onDone={() => { setKorektaDla(null); odswiez(); }} />
      )}
      {rozliczDla && (
        <RozliczModal w={rozliczDla} onClose={() => setRozliczDla(null)} onDone={() => { setRozliczDla(null); odswiez(); }} />
      )}
      {konfiguracjaOtwarta && (
        <KonfiguracjaLimitowModal onClose={() => setKonfiguracjaOtwarta(false)} onDone={() => { setKonfiguracjaOtwarta(false); odswiez(); }} />
      )}
    </div>
  );
};

const PodsumowanieRoczne = ({ domyslnyRok, onWybierzPracownika }) => {
  const [rok, setRok] = useState(domyslnyRok);
  const { data, isLoading, isError } = useQuery(['nadgodziny-podsumowanie', rok], () => nadgodzinyService.getPodsumowanie(rok), { staleTime: 60000 });
  const pracownicy = data?.pracownicy || [];
  const limit = data?.konfiguracja?.limitRoczny;
  const lata = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() + 1 - i);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Scale className="h-4 w-4 text-blue-600" />
          Nadgodziny pracowników w roku vs limit{limit ? ` ${formatGodziny(limit)}` : ''}
          <span className="text-xs font-normal text-gray-500">(zatwierdzone + oczekujące; po potwierdzeniu wykonania liczą się faktyczne godziny)</span>
        </p>
        <select value={rok} onChange={(e) => setRok(Number(e.target.value))} className="input max-w-[120px]">
          {lata.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      {isLoading ? (
        <div className="p-6"><LoadingSpinner size="sm" /></div>
      ) : isError ? (
        <p className="p-4 text-sm text-red-700">Nie udało się pobrać podsumowania.</p>
      ) : pracownicy.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">Brak zatwierdzonych ani oczekujących nadgodzin w {rok} r.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left">Pracownik</th>
                <th className="px-4 py-2 text-left">Dział</th>
                <th className="px-4 py-2 text-right">Zatwierdzone</th>
                <th className="px-4 py-2 text-right">Oczekujące</th>
                <th className="px-4 py-2 text-right">Wykonane</th>
                <th className="px-4 py-2 text-left min-w-[200px]">Wykorzystanie limitu</th>
                <th className="px-4 py-2 text-right">Pozostało</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pracownicy.map((p) => (
                <tr key={p.pracownikId} onClick={() => onWybierzPracownika?.(p)} className="hover:bg-blue-50 cursor-pointer" title="Pokaż wnioski tego pracownika">
                  <td className="px-4 py-2 font-medium text-gray-900">{p.nazwisko} {p.imie}</td>
                  <td className="px-4 py-2 text-gray-600">{p.dzial || '—'}</td>
                  <td className="px-4 py-2 text-right">{formatGodziny(p.zatwierdzone)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{formatGodziny(p.oczekujace)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{formatGodziny(p.wykonane)}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className={`h-full rounded-full ${klasaPaska(p.procent)}`} style={{ width: `${Math.min(100, p.procent)}%` }} />
                      </div>
                      <span className={`text-xs font-medium w-24 text-right ${p.przekroczony ? 'text-red-700' : p.ostrzezenie ? 'text-amber-700' : 'text-gray-700'}`}>
                        {formatGodziny(p.razem)} ({p.procent}%)
                      </span>
                    </div>
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${p.przekroczony ? 'text-red-700' : 'text-gray-900'}`}>
                    {p.przekroczony ? `przekroczono o ${formatGodziny(Math.round((p.razem - p.limit) * 100) / 100)}` : formatGodziny(p.pozostalo)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const KonfiguracjaLimitowModal = ({ onClose, onDone }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery('nadgodziny-konfiguracja', nadgodzinyService.getKonfiguracja);
  const [form, setForm] = useState(null);
  const konfiguracja = data?.konfiguracja;
  const wartosci = form || (konfiguracja ? {
    limitRoczny: konfiguracja.limitRoczny,
    progOstrzezeniaProcent: konfiguracja.progOstrzezeniaProcent,
    limitTygodniowy: konfiguracja.limitTygodniowy
  } : null);
  const set = (pole) => (e) => setForm({ ...wartosci, [pole]: e.target.value });

  const mutation = useMutation(() => nadgodzinyService.zapiszKonfiguracje(wartosci), {
    onSuccess: () => {
      queryClient.invalidateQueries('nadgodziny-konfiguracja');
      queryClient.invalidateQueries('nadgodziny-podsumowanie');
      queryClient.invalidateQueries('nadgodziny-limity');
      toast.success('Limity nadgodzin zostały zapisane');
      onDone();
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się zapisać limitów')
  });

  const poleLiczbowe = (label, pole, opis, min, max) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input type="number" min={min} max={max} step="1" value={wartosci[pole]} onChange={set(pole)} className="input max-w-[160px]" />
      <p className="text-xs text-gray-500 mt-1">{opis}</p>
    </div>
  );

  return (
    <Modal title="Limity nadgodzin (Kodeks pracy)" onClose={onClose} maxWidth="max-w-lg">
      {isLoading || !wartosci ? <LoadingSpinner size="sm" /> : (
        <div className="space-y-4">
          {poleLiczbowe('Roczny limit nadgodzin (godz.)', 'limitRoczny', 'Art. 151 § 3 KP: ustawowo 150 godzin w roku kalendarzowym; układ zbiorowy lub regulamin może ustalić inny limit (maks. 416 h). Powyżej limitu pracownik nie złoży wniosku, a przełożony nie wyrazi zgody.', 1, 416)}
          {poleLiczbowe('Próg ostrzeżenia (% limitu)', 'progOstrzezeniaProcent', 'Od tego procentu wykorzystania limitu pracownik i przełożony widzą żółte ostrzeżenie.', 1, 100)}
          {poleLiczbowe('Limit tygodniowy (godz.)', 'limitTygodniowy', 'Art. 131 KP: przeciętnie 48 godzin pracy tygodniowo, czyli ok. 8 nadgodzin. Powyżej tej sumy w tygodniu ISO przełożony widzi ostrzeżenie (bez blokady).', 1, 48)}
          {konfiguracja?.zmienilImieNazwisko && (
            <p className="text-xs text-gray-500">Ostatnia zmiana: {konfiguracja.zmienilImieNazwisko}, {formatDataGodzina(konfiguracja.updatedAt)}</p>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button onClick={() => mutation.mutate()} disabled={!wartosci || mutation.isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : <Settings className="h-4 w-4" />}Zapisz limity
        </button>
      </div>
    </Modal>
  );
};

const DecyzjaModal = ({ w, user, onClose, onDone }) => {
  const sugestia = sugerowanyDodatek(w.planowanaData, w.godzinaOd, w.godzinaDo);
  const [decyzja, setDecyzja] = useState('zatwierdzony');
  const [sposob, setSposob] = useState(w.wnioskujeOCzasWolny ? 'czas_wolny' : 'wyplata');
  const [dodatek, setDodatek] = useState(sugestia.procent);
  const [uwagi, setUwagi] = useState('');
  const [powod, setPowod] = useState('');
  const [uzasadnieniePoTerminie, setUzasadnieniePoTerminie] = useState('');
  const [podpis, setPodpis] = useState('');
  const [uzasadnienieLimitu, setUzasadnienieLimitu] = useState('');
  const poTerminie = czyPoczatekMinal(w.planowanaData, w.godzinaOd);
  const { data: limityData, isFetching: limityLoading } = useQuery(['nadgodziny-limity-wniosku', w._id], () => nadgodzinyService.getLimityWniosku(w._id), { retry: 1 });
  const stanLimitow = limityData?.stan;
  const ponadLimit = !!stanLimitow?.rok?.przekroczonyPoZatwierdzeniu;
  const mozeMimoLimitu = !!limityData?.mozeZatwierdzicMimoLimitu;
  const blokadaLimitu = decyzja === 'zatwierdzony' && ponadLimit && !mozeMimoLimitu;

  const mutation = useMutation(() => nadgodzinyService.podejmijDecyzje(w._id, {
    decyzja,
    podpisPrzełożonego: podpis,
    uwagiLimity: uwagi.trim(),
    uzasadnieniePoTerminie: uzasadnieniePoTerminie.trim(),
    ...(decyzja === 'zatwierdzony' && ponadLimit ? { uzasadnieniePrzekroczeniaLimitu: uzasadnienieLimitu.trim() } : {}),
    ...(decyzja === 'zatwierdzony'
      ? { sposobRozliczenia: sposob, ...(sposob === 'wyplata' ? { dodatekProcent: dodatek } : {}) }
      : { powodOdrzucenia: powod.trim() })
  }), {
    onSuccess: () => { toast.success('Decyzja została zapisana — pracownik i zarząd otrzymali powiadomienie'); onDone(); },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się zapisać decyzji')
  });

  const gotowe = podpis && (decyzja === 'zatwierdzony' || powod.trim()) && (!poTerminie || uzasadnieniePoTerminie.trim())
    && !blokadaLimitu && (decyzja !== 'zatwierdzony' || !ponadLimit || uzasadnienieLimitu.trim());
  const radio = (checked) => `flex items-start gap-2 rounded-lg border-2 px-3 py-2.5 cursor-pointer ${checked ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`;

  return (
    <Modal title="Decyzja bezpośredniego przełożonego" onClose={onClose}>
      <div className="mb-5 p-4 bg-gray-50 rounded-lg text-sm space-y-1">
        <p><span className="text-gray-500">Pracownik:</span> <strong>{w.pracownikImie} {w.pracownikNazwisko}</strong></p>
        <p><span className="text-gray-500">Termin:</span> {formatData(w.planowanaData)}, {w.godzinaOd} – {w.godzinaDo} ({formatGodziny(w.szacowanaLiczbaGodzin)})</p>
        {w.projekt && <p><span className="text-gray-500">Projekt:</span> {w.projekt}</p>}
        <p className="whitespace-pre-wrap"><span className="text-gray-500">Zakres prac:</span> {w.zakresPrac}</p>
        <p className="whitespace-pre-wrap"><span className="text-gray-500">Potrzeba biznesowa:</span> {w.potrzebaBiznesowa}</p>
        {w.wnioskujeOCzasWolny && <p className="text-blue-800 font-medium">Pracownik wnioskuje o czas wolny 1:1.</p>}
      </div>

      <div className="mb-4">
        <PanelLimitowNadgodzin stan={stanLimitow} tryb="decyzja" ladowanie={limityLoading} />
        {decyzja === 'zatwierdzony' && ponadLimit && (
          mozeMimoLimitu ? (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-900">
              <p>Jako administrator możesz zatwierdzić mimo przekroczenia limitu — uzasadnienie trafi do zarządu i do dokumentacji wniosku.</p>
              <textarea
                value={uzasadnienieLimitu}
                onChange={(e) => setUzasadnienieLimitu(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="Uzasadnienie zgody ponad roczny limit *"
                className="mt-2 w-full border border-red-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
          ) : (
            <p className="mt-2 text-sm text-red-800 font-medium">Nie możesz wyrazić zgody — odmów albo zwróć wniosek do poprawy. Zgodę ponad limit może wydać tylko administrator.</p>
          )
        )}
      </div>

      {poTerminie && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          <p className="flex gap-2"><AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />Planowany początek pracy już minął. Decyzja zostanie oznaczona jako podjęta po terminie (widoczne dla zarządu) — uzasadnij ją.</p>
          <textarea
            value={uzasadnieniePoTerminie}
            onChange={(e) => setUzasadnieniePoTerminie(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Dlaczego decyzja zapada po planowanym starcie? *"
            className="mt-2 w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white"
          />
        </div>
      )}

      <p className="text-sm font-medium mb-2">Decyzja</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        <label className={radio(decyzja === 'zatwierdzony')}>
          <input type="radio" checked={decyzja === 'zatwierdzony'} onChange={() => setDecyzja('zatwierdzony')} className="mt-1" />
          <span className="text-sm"><strong>WYRAŻAM ZGODĘ</strong><br /><span className="text-gray-500">polecam wykonanie pracy</span></span>
        </label>
        <label className={radio(decyzja === 'odrzucony')}>
          <input type="radio" checked={decyzja === 'odrzucony'} onChange={() => setDecyzja('odrzucony')} className="mt-1" />
          <span className="text-sm"><strong>NIE WYRAŻAM ZGODY</strong></span>
        </label>
      </div>

      {decyzja === 'zatwierdzony' ? (
        <>
          <p className="text-sm font-medium mb-2">Sposób rozliczenia</p>
          <div className="space-y-2 mb-5">
            <label className={radio(sposob === 'wyplata')}>
              <input type="radio" checked={sposob === 'wyplata'} onChange={() => setSposob('wyplata')} className="mt-1" />
              <span className="text-sm flex-1">
                <strong>Wypłata wynagrodzenia wraz z dodatkiem</strong>
                {sposob === 'wyplata' && (
                  <span className="flex flex-wrap items-center gap-3 mt-2">
                    {[50, 100].map((p) => (
                      <label key={p} className="flex items-center gap-1.5">
                        <input type="radio" checked={dodatek === p} onChange={() => setDodatek(p)} />{p}%
                      </label>
                    ))}
                    {sugestia.powod && <span className="text-xs text-gray-500">Podpowiedź: {sugestia.powod}</span>}
                  </span>
                )}
              </span>
            </label>
            <label className={radio(sposob === 'czas_wolny')}>
              <input type="radio" checked={sposob === 'czas_wolny'} onChange={() => setSposob('czas_wolny')} className="mt-1" />
              <span className="text-sm">
                <strong>Udzielenie czasu wolnego</strong>
                <span className="block text-gray-500">
                  {w.wnioskujeOCzasWolny
                    ? 'W wymiarze 1:1 — pracownik złożył wniosek o czas wolny.'
                    : 'Pracownik nie wnioskował o czas wolny — z inicjatywy pracodawcy przysługuje 1,5 godziny za każdą nadgodzinę (art. 151² § 2 KP).'}
                </span>
              </span>
            </label>
          </div>
        </>
      ) : (
        <div className="mb-5">
          <label className="block text-sm font-medium mb-2">Powód braku zgody *</label>
          <textarea value={powod} onChange={(e) => setPowod(e.target.value)} rows={3} maxLength={2000} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      )}

      <div className="mb-5">
        <label className="block text-sm font-medium mb-2">Uwagi / limity <span className="text-gray-400 font-normal">(opcjonalnie)</span></label>
        <textarea
          value={uwagi}
          onChange={(e) => setUwagi(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="np. maksymalnie do godz. 19:00, tylko zadania z punktu 1"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium mb-2">Podpis przełożonego *</label>
        <SignaturePad onSignatureChange={setPodpis} width={400} height={140} signature={podpis || undefined} />
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button
          onClick={() => mutation.mutate()}
          disabled={!gotowe || mutation.isLoading}
          className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 ${decyzja === 'zatwierdzony' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
        >
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : decyzja === 'zatwierdzony' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {decyzja === 'zatwierdzony' ? 'Zatwierdź nadgodziny' : 'Odmów zgody'}
        </button>
      </div>
    </Modal>
  );
};

const ZwrotModal = ({ w, onClose, onDone }) => {
  const [komentarz, setKomentarz] = useState('');
  const mutation = useMutation(() => nadgodzinyService.zwrocDoPracownika(w._id, komentarz.trim()), {
    onSuccess: () => { toast.success('Wniosek zwrócony do poprawy'); onDone(); },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się zwrócić wniosku')
  });
  return (
    <Modal title="Zwróć wniosek do poprawy" onClose={onClose} maxWidth="max-w-lg">
      <p className="text-sm text-gray-600 mb-4">
        Wniosek wróci do pracownika <strong>{w.pracownikImie} {w.pracownikNazwisko}</strong> z Twoimi uwagami. Po poprawce trafi ponownie do Ciebie.
      </p>
      <label className="block text-sm font-medium mb-2">Co należy poprawić? *</label>
      <textarea value={komentarz} onChange={(e) => setKomentarz(e.target.value)} rows={4} maxLength={2000} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button onClick={() => mutation.mutate()} disabled={!komentarz.trim() || mutation.isLoading} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2">
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : <RotateCcw className="h-4 w-4" />}Zwróć do pracownika
        </button>
      </div>
    </Modal>
  );
};

const PrzekazModal = ({ w, onClose, onDone }) => {
  const [nowy, setNowy] = useState('');
  const [powod, setPowod] = useState('');
  const { data, isLoading } = useQuery('nadgodziny-osoby-do-przekazania', nadgodzinyService.getOsobyDoPrzekazania, { staleTime: 60000 });
  const osoby = useMemo(
    () => (data?.osoby || []).filter((u) => u._id !== idOsoby(w.przełożony) && u._id !== idOsoby(w.createdBy)),
    [data, w]
  );
  const mutation = useMutation(() => nadgodzinyService.przekaz(w._id, nowy, powod.trim()), {
    onSuccess: (r) => { toast.success(r?.message || 'Wniosek został przekazany'); onDone(); },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się przekazać wniosku')
  });
  return (
    <Modal title="Przekaż wniosek innej osobie" onClose={onClose} maxWidth="max-w-lg">
      <p className="text-sm text-gray-600 mb-4">
        Wniosek <strong>{w.pracownikImie} {w.pracownikNazwisko}</strong> ({formatData(w.planowanaData)}, {w.godzinaOd} – {w.godzinaDo}) rozpatruje obecnie <strong>{osoba(w.przełożony)}</strong>.
        Nowa osoba otrzyma powiadomienie i podejmie decyzję; pracownik zostanie poinformowany.
      </p>
      <label className="block text-sm font-medium mb-2">Nowy przełożony *</label>
      {isLoading ? <LoadingSpinner size="sm" /> : (
        <SearchableUserSelect users={osoby} value={nowy} onChange={setNowy} clearLabel="— Wybierz osobę —" />
      )}
      <label className="block text-sm font-medium mb-2 mt-4">Powód <span className="text-gray-400 font-normal">(opcjonalnie)</span></label>
      <textarea value={powod} onChange={(e) => setPowod(e.target.value)} rows={2} maxLength={1000} placeholder="np. przełożony na zwolnieniu lekarskim" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button onClick={() => mutation.mutate()} disabled={!nowy || mutation.isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : <ArrowRightLeft className="h-4 w-4" />}Przekaż
        </button>
      </div>
    </Modal>
  );
};

const PolaGodzin = ({ od, setOd, doGodz, setDoGodz, godziny }) => (
  <div className="grid grid-cols-3 gap-3 mb-3">
    <div>
      <label className="block text-xs text-gray-500 mb-1">Od godz.</label>
      <input type="time" value={od} onChange={(e) => setOd(e.target.value)} className="input" />
    </div>
    <div>
      <label className="block text-xs text-gray-500 mb-1">Do godz.</label>
      <input type="time" value={doGodz} onChange={(e) => setDoGodz(e.target.value)} className="input" />
    </div>
    <div>
      <label className="block text-xs text-gray-500 mb-1">Razem</label>
      <p className="py-2 text-sm font-semibold">{formatGodziny(godziny)}</p>
    </div>
  </div>
);

const RealizacjaModal = ({ w, onClose, onDone }) => {
  const [od, setOd] = useState(w.godzinaOd);
  const [doGodz, setDoGodz] = useState(w.godzinaDo);
  const [niewykonano, setNiewykonano] = useState(false);
  const [uwagi, setUwagi] = useState('');
  const godziny = obliczLiczbeGodzin(od, doGodz);
  const przekroczenie = !niewykonano && godziny > w.szacowanaLiczbaGodzin;

  const mutation = useMutation(() => nadgodzinyService.potwierdzRealizacje(w._id, {
    niewykonano,
    ...(niewykonano ? {} : { faktycznaGodzinaOd: od, faktycznaGodzinaDo: doGodz }),
    realizacjaUwagi: uwagi.trim()
  }), {
    onSuccess: (r) => { toast.success(r?.message || 'Wykonanie potwierdzone'); onDone(); },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się zapisać potwierdzenia')
  });

  return (
    <Modal title="Potwierdź wykonanie nadgodzin" onClose={onClose} maxWidth="max-w-lg">
      <p className="text-sm text-gray-600 mb-4">
        Zatwierdzono: {formatData(w.planowanaData)}, {w.godzinaOd} – {w.godzinaDo} ({formatGodziny(w.szacowanaLiczbaGodzin)}).
        Podaj faktycznie przepracowany czas — na tej podstawie HR rozliczy nadgodziny. Po potwierdzeniu zmiany może wprowadzić tylko przełożony lub HR.
      </p>
      <label className="flex items-center gap-2 mb-4 text-sm">
        <input type="checkbox" checked={niewykonano} onChange={(e) => setNiewykonano(e.target.checked)} />
        Nadgodziny nie zostały wykonane
      </label>
      {!niewykonano && <PolaGodzin od={od} setOd={setOd} doGodz={doGodz} setDoGodz={setDoGodz} godziny={godziny} />}
      {przekroczenie && (
        <p className="mb-3 text-xs text-amber-800 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5" />
          Czas przekracza zatwierdzony — opisz powód w uwagach. Przełożony musi zaakceptować przekroczenie, zanim HR rozliczy nadgodziny.
        </p>
      )}
      <label className="block text-sm font-medium mb-2">Uwagi {przekroczenie ? '*' : <span className="text-gray-400 font-normal">(opcjonalnie)</span>}</label>
      <textarea value={uwagi} onChange={(e) => setUwagi(e.target.value)} rows={3} maxLength={1000} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isLoading || (przekroczenie && !uwagi.trim()) || (!niewykonano && (!od || !doGodz))}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : <ClipboardCheck className="h-4 w-4" />}Potwierdź
        </button>
      </div>
    </Modal>
  );
};

const KorektaModal = ({ w, onClose, onDone }) => {
  const [od, setOd] = useState(w.faktycznaGodzinaOd || w.godzinaOd);
  const [doGodz, setDoGodz] = useState(w.faktycznaGodzinaDo || w.godzinaDo);
  const [niewykonano, setNiewykonano] = useState(false);
  const [uzasadnienie, setUzasadnienie] = useState('');
  const godziny = obliczLiczbeGodzin(od, doGodz);

  const mutation = useMutation(() => nadgodzinyService.korygujRealizacje(w._id, {
    niewykonano,
    ...(niewykonano ? {} : { faktycznaGodzinaOd: od, faktycznaGodzinaDo: doGodz }),
    uzasadnienie: uzasadnienie.trim()
  }), {
    onSuccess: () => { toast.success('Faktyczne godziny zostały skorygowane — pracownik otrzymał powiadomienie'); onDone(); },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się zapisać korekty')
  });

  return (
    <Modal title="Korekta faktycznych godzin" onClose={onClose} maxWidth="max-w-lg">
      <p className="text-sm text-gray-600 mb-4">
        Pracownik potwierdził: <strong>{opisFaktycznych(w)}</strong> (zatwierdzono {formatGodziny(w.szacowanaLiczbaGodzin)}).
        Korekta jest zapisywana w historii wniosku i widoczna dla pracownika.
      </p>
      <label className="flex items-center gap-2 mb-4 text-sm">
        <input type="checkbox" checked={niewykonano} onChange={(e) => setNiewykonano(e.target.checked)} />
        Nadgodziny nie zostały wykonane (0 godz.)
      </label>
      {!niewykonano && <PolaGodzin od={od} setOd={setOd} doGodz={doGodz} setDoGodz={setDoGodz} godziny={godziny} />}
      <label className="block text-sm font-medium mb-2">Uzasadnienie korekty *</label>
      <textarea value={uzasadnienie} onChange={(e) => setUzasadnienie(e.target.value)} rows={3} maxLength={1000} placeholder="np. według ewidencji wejść/wyjść" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isLoading || !uzasadnienie.trim() || (!niewykonano && (!od || !doGodz))}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : <Pencil className="h-4 w-4" />}Zapisz korektę
        </button>
      </div>
    </Modal>
  );
};

const RozliczModal = ({ w, onClose, onDone }) => {
  const [uwagi, setUwagi] = useState('');
  const mutation = useMutation(() => nadgodzinyService.rozlicz(w._id, uwagi.trim()), {
    onSuccess: () => { toast.success('Nadgodziny zostały rozliczone'); onDone(); },
    onError: (error) => toast.error(error.response?.data?.message || 'Nie udało się rozliczyć')
  });
  return (
    <Modal title="Rozlicz nadgodziny" onClose={onClose} maxWidth="max-w-lg">
      <div className="text-sm text-gray-600 mb-4 space-y-1">
        <p><strong>{w.pracownikImie} {w.pracownikNazwisko}</strong>, {formatData(w.planowanaData)}</p>
        <p>Faktycznie przepracowano: <strong>{opisFaktycznych(w)}</strong></p>
        <p>Sposób rozliczenia: <strong>{opisRozliczenia(w)}</strong></p>
        <p className="text-xs text-gray-500">Po rozliczeniu korekta godzin nie będzie już możliwa.</p>
      </div>
      <label className="block text-sm font-medium mb-2">Uwagi <span className="text-gray-400 font-normal">(np. okres wypłaty / termin odbioru czasu wolnego)</span></label>
      <textarea value={uwagi} onChange={(e) => setUwagi(e.target.value)} rows={2} maxLength={1000} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
        <button onClick={() => mutation.mutate()} disabled={mutation.isLoading} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
          {mutation.isLoading ? <LoadingSpinner size="sm" /> : <BadgeCheck className="h-4 w-4" />}Oznacz jako rozliczone
        </button>
      </div>
    </Modal>
  );
};

export default Nadgodziny;
